import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi, type ChannelCloseoutListItem } from '@/shared/api';
import type { CloseoutAction } from './ChannelCloseoutRow';

const SUCCESS_TEXT: Record<CloseoutAction, string> = {
  retry: 'Kanalregel erneut vorgemerkt',
  reconcile: 'Abgleich angestoßen',
  reissue: 'Kanalregel neu vorgemerkt',
  remove: 'Aufheben vorgemerkt',
};

/** Jede Aktion trägt die Revision, die der Betrieb gesehen hat; eine fremde Änderung lehnt der Server ab. */
export function useChannelCloseoutAction(propertyGroupId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const closeouts = api.availabilityDistribution.closeouts;
  return useMutation({
    mutationFn: ({ item, action }: { item: ChannelCloseoutListItem; action: CloseoutAction }) => {
      const body = { expectedRevision: item.desiredRevision };
      if (action === 'retry') return closeouts.retry(propertyGroupId, item.id, body);
      if (action === 'reconcile') return closeouts.reconcile(propertyGroupId, item.id, body);
      if (action === 'reissue') {
        return closeouts.confirmAbsentAndReissue(propertyGroupId, item.id, body);
      }
      return closeouts.remove(propertyGroupId, item.id, item.desiredRevision);
    },
    onSuccess: async (_, { action }) => {
      await queryClient.invalidateQueries({ queryKey: ['availability-distribution'] });
      toast.success(SUCCESS_TEXT[action]);
    },
    onError: async (error) => {
      const problem = await toApiError(error);
      toast.error(problem.detail ?? problem.title);
      // Eine fremde Änderung (409) zeigt erst die neu geladene Liste; sonst scheitert jeder Klick erneut.
      if (problem.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['availability-distribution'] });
      }
    },
  });
}
